import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

/*
 * Not used by anything in this file, and extended here on purpose.
 *
 * `OpeningHoursCard` parses the stored windows with `dayjs('09:00', 'HH:mm')`,
 * which without this plugin ignores the format and returns Invalid Date. It has
 * been working only because `@rc-component/picker` extends the plugin onto the
 * global dayjs as an import side effect — and that module now carries a comment
 * saying it is deliberately moving to an internal instance to stop leaking its
 * plugins. The day it finishes, every opening-hours row would silently become
 * Invalid Date. This file is imported app-wide through `format.ts`, so claiming
 * the dependency here is what makes it ours rather than a coincidence.
 */
dayjs.extend(customParseFormat);

/**
 * One clock for the whole panel.
 *
 * Before this existed, every date in the admin was rendered with plain
 * `dayjs(value).format(...)`, which means **the timezone of whatever laptop the
 * page happens to be open on**. For a marketplace that trades in one country
 * that is not a nuance, it is a defect with three distinct faces:
 *
 *   A timestamp read differently by two people. An order placed at 23:10 IST
 *   reads as 17:40 to an operator whose machine is on UTC, and there is nothing
 *   on the screen admitting that the two disagree.
 *
 *   A value WRITTEN wrong. `OpeningHoursCard` sent closures as
 *   `picked.toISOString()` — the picked wall clock converted to UTC through the
 *   browser's zone. Declare "closed 22 Sep 09:00–18:00" on a UTC machine and
 *   the shop actually goes dark from 14:30 IST.
 *
 *   Day boundaries that land in the wrong day. `daysUntil` compared
 *   `startOf('day')` on the browser's clock, so a batch expiring "tomorrow" in
 *   Kolkata could report as expiring today, or in two days.
 *
 * The value is NOT decided here. The server owns it — `PLATFORM_TIMEZONE` in
 * its environment — because that is what its SQL compares a shop's stored
 * `09:00` against, and it hands the value down on `/auth/me`. The constant
 * below is only the fallback for the moment before that response lands.
 *
 * One value for the whole platform, not one per pharmacy: India has a single
 * timezone, and a per-shop zone would be inventing a problem this product does
 * not have. If that ever changes, the zone belongs on the pharmacy record —
 * "9am" means the shop's own nine — and this becomes the default.
 */

/** India. The product's home, and the fallback whenever a stored zone is junk. */
export const DEFAULT_TIMEZONE = 'Asia/Kolkata';

let current: string = DEFAULT_TIMEZONE;

/**
 * Is this a zone the runtime actually knows?
 *
 * The value arrives from a generic key/value config table that accepts any
 * JSON, so it can be a typo, a stale name, or something that was never a zone.
 * `Intl` throwing is the only reliable test, and an unknown zone must not be
 * allowed to take the whole panel's date rendering down with it.
 */
export function isValidTimezone(zone: string): boolean {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export function platformTimezone(): string {
  return current;
}

/** Returns whether it took. A rejected zone leaves the last good one in place. */
export function setPlatformTimezone(zone: string): boolean {
  if (!isValidTimezone(zone)) return false;
  current = zone;
  return true;
}

/** What the machine this page is open on thinks it is. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * `UTC+05:30`, rather than `IST`.
 *
 * Abbreviations are not unique — IST is India, Israel and Ireland depending on
 * who is saying it — and the whole point of showing the zone is to remove an
 * ambiguity, not to move it somewhere else.
 */
export function zoneOffsetLabel(zone: string = current): string {
  const minutes = dayjs().tz(zone).utcOffset();
  const sign = minutes < 0 ? '-' : '+';
  const absolute = Math.abs(minutes);
  const hours = String(Math.floor(absolute / 60)).padStart(2, '0');
  return `UTC${sign}${hours}:${String(absolute % 60).padStart(2, '0')}`;
}

/** An instant from the server, read on the platform's clock. */
export function inZone(value?: string | number | Date | Dayjs | null): Dayjs {
  return dayjs(value ?? undefined).tz(current);
}

/** Now, on the platform's clock. */
export function nowInZone(): Dayjs {
  return dayjs().tz(current);
}

/**
 * A wall clock somebody picked, resolved to the instant it names HERE.
 *
 * antd's pickers hand back a `Dayjs` built on the browser's zone, so the only
 * part worth keeping is the face of the clock — 22 Sep, 09:00. This re-reads
 * those digits as platform-local and returns the real moment, which is what
 * makes a closure declared from a laptop in another country still start when
 * the shutter actually comes down.
 */
export function wallClockToInstant(picked: Dayjs, zone: string = current): Dayjs {
  return dayjs.tz(picked.format('YYYY-MM-DD HH:mm:ss'), zone);
}

/**
 * The reverse: an instant, dressed as a wall clock the browser's pickers will
 * display unchanged. For loading a stored value back into a `DatePicker`.
 */
export function instantToWallClock(value: string | Dayjs, zone: string = current): Dayjs {
  return dayjs(dayjs(value).tz(zone).format('YYYY-MM-DD HH:mm:ss'));
}
